import React, { useEffect, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import AobInvoice, { InvoiceDetails } from 'types/aobInvoice'
import Logo from 'components/headerBarV2/Logo'

// Optional payment profile interfaces - would be more fully defined in a real implementation
interface PaymentProfile {
  customerPaymentProfileId: string;
  payment?: {
    creditCard?: {
      cardNumber?: string;
    };
    bankAccount?: {
      accountNumber?: string;
    };
  };
}

interface CardDetails {
  cardNumber?: string;
}

interface BankDetails {
  accountNumber?: string;
}

interface PaymentBreakdownProps {
  invoiceResponse: AobInvoice;
}

/**
 * PaymentBreakdown component displays an order summary with payment details
 */
function PaymentBreakdown({ invoiceResponse }: PaymentBreakdownProps ): JSX.Element {
  const selectedPaymentProfile: string | null = null
  const paymentProfiles: PaymentProfile[] = []
  const newCardDetails: CardDetails = {}
  const newBankDetails: BankDetails = {}

  const screenshotRef = useRef<HTMLDivElement>( null )
  const [ screenshotData, setScreenshotData ] = useState<string | null>( null )

  useEffect( () => {
    takeScreenshot()
  }, [] )

  const now = new Date()

  // Find profile based on selected payment profile ID
  const savedProfile = paymentProfiles.find(
    ( profile: PaymentProfile ) => profile.customerPaymentProfileId === selectedPaymentProfile
  )

  const isCreditCard =
    Boolean( savedProfile?.payment?.creditCard ) ||
    selectedPaymentProfile === `New Credit Card`

  // Get last 4 digits of payment method
  const last4 = savedProfile
    ? isCreditCard
      ? savedProfile.payment?.creditCard?.cardNumber?.slice( -4 ) || ``
      : savedProfile.payment?.bankAccount?.accountNumber?.slice( -4 ) || ``
    : isCreditCard
      ? newCardDetails?.cardNumber?.slice( -4 ) || ``
      : newBankDetails?.accountNumber?.slice( -4 ) || ``

  const formatCurrency = ( amount: number ): string => {
    // Simple currency formatter in lieu of the imported utility
    return `$${amount?.toFixed( 2 )}`
  }

  const takeScreenshot = (): void => {
    if ( screenshotRef.current ) {
      html2canvas( screenshotRef.current, {
        useCORS: true // in case you have images stored in your application
      })
        .then( ( canvas: HTMLCanvasElement ) => {
          const data = canvas.toDataURL( `image/png` )
          setScreenshotData( data )
        })
    }
  }

  return (
    <div className="mt-8 mb-12 rounded-md bg-[#F3F5F7] px-6 py-[34px] max-w-xl mx-auto ">
      <div className="py-5 px-5" ref={screenshotRef}>
        <div className="mb-12 mt-2 flex justify-center">
          <Logo withoutLink />
        </div>
        <p className="mb-6 text-xl md:text-2xl font-semibold text-left">
          {`Payment Summary`}
        </p>
        <div className="inline-block">
          <div className="font-light flex flex-col gap-4 mb-5">
            {invoiceResponse?.details?.map( ( details: InvoiceDetails ) => (
              <div className="float-left w-full" key={details.dme_id}>
                <span className="float-left w-9/12">
                  {details.item_description}
                </span>
                <span className="float-right">
                  {formatCurrency( details?.patient_owes )}
                </span>
              </div>
            ) )}
          </div>
          <div className="mb-5">
            <p className="font-semibold text-xl md:text-2xl flex justify-between">
              <span>{`Total:`}</span>
              <span>{`$${invoiceResponse?.total_patient_owes?.toFixed( 2 )}`}</span>
            </p>
          </div>
        </div>
        {
          ( invoiceResponse?.total_patient_owes ?? 0 ) > 0 &&
          selectedPaymentProfile && (
            <p className="font-semibold mb-5 text-xl md:text-2xl">
              {`Paid with ${isCreditCard ? `Credit Card` : `Bank Account`} ending in ${last4}`}
            </p>
          )}
      </div>
      <div className="flex items-center pb-5">
        <button
          className="btn btn-tertiary"
          disabled={!screenshotData}
        >
          {screenshotData ? (
            <a
              download={`Aeroflow_Sleep_Order_Summary_${now.toLocaleDateString()}`}
              href={screenshotData}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline"
            >
              {`Download Order Summary`}
            </a>
          ) : (
            <span className="flex items-center">
              <span className="animate-spin mr-2 h-4 w-4 border-t-2 border-blue-500 border-r-2 rounded-full"></span>
              <span className="px-2">{`Capturing Order Summary...`}</span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

export default PaymentBreakdown